import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import TextFilter from '../../../../commonComponents/lists/filters/TextFilter';
import FormInput from '../../../../commonComponents/forms/FormInput';
import DateRange from '../../../../commonComponents/forms/inputs/DateRange';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import translate from '../../../../commonComponents/translate';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  isNeoCardCompetency,
  normalizeCompetencyName,
  competencyOrder,
} from './neofinCompetency';

function ReportFilters(props) {
  const { organization } = useContext(ConfigsContext);
  const { competenciesDomains, skillLevels, directSelling } = organization || {};
  const { t, filters, onFilterChange, onDateChange, countries = [] } = props;
  const {
    search,
    createDateStart,
    createDateEnd,
    shippingCountry,
    access,
    active,
    forecastLevel,
    level,
    sponsoredBy,
    finAppStatus,
    vaGt0,
    developerOnly,
    noNeocard,
    noLicense,
    rankMin,
    rankMax,
  } = filters;

  const levels = useMemo(
    () =>
      Object.keys(skillLevels || {}).map(l => ({
        id: l,
        text: skillLevels[l].name,
      })),
    [skillLevels],
  );
  const inputProps = useCallback(
    field => {
      switch (field) {
        case 'shippingCountry':
          return {
            searchable: true,
            allowClear: true,
            items: countries.map(c => ({
              id: c.code,
              text: `${c.flagEmoji} ${c.name}`,
            })),
          };
        case 'businessLicense':
          return {
            allowClear: true,
            searchable: false,
            items: [
              { id: true, text: t('yes') },
              { id: false, text: t('no') },
            ],
          };
        case 'access': {
          // BRAVON-6291: recognize legacy AND new-generation (`_0626`)
          // competencies, and collapse old + new of the same card into a
          // single option (the backend expands it to every matching domain).
          const byCanonical = new Map();
          (competenciesDomains || [])
            .filter(c => isNeoCardCompetency(c.translations.en))
            .forEach(c => {
              const canonical = normalizeCompetencyName(c.translations.en);
              const isBase = c.translations.en === canonical;
              const existing = byCanonical.get(canonical);
              if (!existing) {
                byCanonical.set(canonical, {
                  id: c._id,
                  text: c.name,
                  order: competencyOrder(canonical),
                  hasBase: isBase,
                });
              } else if (isBase && !existing.hasBase) {
                // Prefer the legacy domain for the option id + label.
                existing.id = c._id;
                existing.text = c.name;
                existing.hasBase = true;
              }
            });
          return {
            allowClear: true,
            searchable: false,
            items: [...byCanonical.values()]
              .sort((a, b) => a.order - b.order)
              .map(({ id, text }) => ({ id, text })),
          };
        }
        case 'active':
          return {
            allowClear: true,
            searchable: false,
            items: [
              { id: true, text: t('yes') },
              { id: false, text: t('no') },
            ],
          };
        case 'forecastLevel':
          return {
            searchable: true,
            allowClear: true,
            items: levels,
          };
        case 'level':
          return {
            searchable: true,
            allowClear: true,
            items: levels,
          };
        case 'sponsoredBy':
          return {
            allowClear: true,
            url: '/user/table/network/list',
          };
        case 'finAppStatus':
          return {
            allowClear: true,
            searchable: false,
            items: [
              { id: 'never', text: t('report.neofin.filter.finAppNever') },
              {
                id: 'freemium',
                text: t('report.neofin.filter.finAppFreemium'),
              },
              { id: 'premium', text: t('report.neofin.filter.finAppPremium') },
            ],
          };
        case 'vaGt0':
        case 'developerOnly':
        case 'noNeocard':
          return {
            allowClear: true,
            searchable: false,
            items: [{ id: true, text: t('yes') }],
          };
        case 'noLicense':
          return {
            allowClear: true,
            searchable: false,
            items: [{ id: true, text: t('yes') }],
          };
        case 'rankMin':
        case 'rankMax':
          return {
            searchable: true,
            allowClear: true,
            items: levels,
          };
        default:
          return {
            items: [],
          };
      }
    },
    [levels, t],
  );
  return (
    <div className="filters-wrapper">
      <TextFilter
        id="searchInput"
        onChange={onFilterChange('search')}
        placeholder={t('search')}
        value={search}
      />
      <DateRange
        directSellingSettings={directSelling}
        id="dateRange"
        placeholder={t('shop.date')}
        startDate={createDateStart}
        endDate={createDateEnd}
        onChange={({ startDate, endDate }) => onDateChange(startDate, endDate)}
        onClear={() => onDateChange(null, null)}
      />
      <FormInput
        id="shippingCountry"
        value={shippingCountry}
        name="shippingCountry"
        placeholder={t('address_countryCode')}
        type="select"
        onChange={onFilterChange('shippingCountry')}
        inputProps={inputProps('shippingCountry')}
      />
      <FormInput
        id="access"
        value={access}
        name="access"
        placeholder={t('access')}
        type="select"
        onChange={onFilterChange('access')}
        inputProps={inputProps('access')}
      />
      <FormInput
        id="active"
        value={active}
        name="active"
        placeholder={t('active')}
        type="select"
        onChange={onFilterChange('active')}
        inputProps={inputProps('active')}
      />
      <FormInput
        id="forecastLevel"
        value={forecastLevel}
        name="forecastLevel"
        placeholder={t('report.neofin.currentLevel')}
        type="select"
        onChange={onFilterChange('forecastLevel')}
        inputProps={inputProps('forecastLevel')}
      />
      <FormInput
        id="level"
        value={level}
        name="level"
        placeholder={t('report.neofin.levelMax')}
        type="select"
        onChange={onFilterChange('level')}
        inputProps={inputProps('level')}
      />
      <FormInput
        id="sponsoredBy"
        name="sponsoredBy"
        placeholder={t('sponsor')}
        value={sponsoredBy}
        onChange={onFilterChange('sponsoredBy')}
        type="users"
        inputProps={inputProps('sponsoredBy')}
      />
      <FormInput
        id="finAppStatus"
        value={finAppStatus}
        name="finAppStatus"
        placeholder={t('report.neofin.filter.finAppStatus')}
        type="select"
        onChange={onFilterChange('finAppStatus')}
        inputProps={inputProps('finAppStatus')}
      />
      <FormInput
        id="vaGt0"
        value={vaGt0}
        name="vaGt0"
        placeholder={t('report.neofin.filter.vaGt0')}
        type="select"
        onChange={onFilterChange('vaGt0')}
        inputProps={inputProps('vaGt0')}
      />
      <FormInput
        id="developerOnly"
        value={developerOnly}
        name="developerOnly"
        placeholder={t('report.neofin.filter.developerOnly')}
        type="select"
        onChange={onFilterChange('developerOnly')}
        inputProps={inputProps('developerOnly')}
      />
      <FormInput
        id="noNeocard"
        value={noNeocard}
        name="noNeocard"
        placeholder={t('report.neofin.filter.noNeocard')}
        type="select"
        onChange={onFilterChange('noNeocard')}
        inputProps={inputProps('noNeocard')}
      />
      <FormInput
        id="noLicense"
        value={noLicense}
        name="noLicense"
        placeholder={t('report.neofin.filter.noLicense')}
        type="select"
        onChange={onFilterChange('noLicense')}
        inputProps={inputProps('noLicense')}
      />
      <FormInput
        id="rankMin"
        value={rankMin}
        name="rankMin"
        placeholder={t('report.neofin.filter.rankMin')}
        type="select"
        onChange={onFilterChange('rankMin')}
        inputProps={inputProps('rankMin')}
      />
      <FormInput
        id="rankMax"
        value={rankMax}
        name="rankMax"
        placeholder={t('report.neofin.filter.rankMax')}
        type="select"
        onChange={onFilterChange('rankMax')}
        inputProps={inputProps('rankMax')}
      />
    </div>
  );
}

ReportFilters.propTypes = {
  t: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  filters: PropTypes.shape({
    search: PropTypes.string,
    createDateStart: PropTypes.number,
    createDateEnd: PropTypes.number,
    shippingCountry: PropTypes.string,
    businessLicense: PropTypes.string,
    access: PropTypes.string,
    active: PropTypes.bool,
    forecastLevel: PropTypes.number,
    level: PropTypes.number,
    sponsoredBy: PropTypes.string,
    finAppStatus: PropTypes.string,
    vaGt0: PropTypes.bool,
    developerOnly: PropTypes.bool,
    noNeocard: PropTypes.bool,
    noLicense: PropTypes.bool,
    rankMin: PropTypes.number,
    rankMax: PropTypes.number,
  }).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType)),
};

ReportFilters.defaultProps = {
  countries: [],
};

export default translate(ReportFilters);
